/**
 * ContractDetailPage · v2.7.35 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Tabbed detail view for a single contract.
 * Route: /admin/contracts/:classificationSlug/:contract_id
 *
 * Tabs:
 *   - Overview          (account info + summary stats + revoke toggle)
 *   - Sub-Accounts      (embedded tree grid · spec section 3)
 *   - Documents         (200 MB multipart uploads + history audit · spec section 4)
 *   - AR Invoices       (open/paid ledger · spec section 5)
 *   - Check Settlement  (pending/cleared/bounced + auto-reconcile · spec section 5)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Briefcase, Plus, Upload, Download, Trash2, FileText, Users, DollarSign,
  CheckCircle2, XCircle, RefreshCw, Clock, AlertTriangle, Archive, FolderLock, Replace,
} from "lucide-react";
import { toast } from "sonner";
import { contractsApi, CLASSIFICATION_LABELS } from "@/lib/contractsApi";
import adminApi from "@/lib/adminApi";

const TABS = [
  { id: "overview",   label: "Overview",         Icon: Briefcase },
  { id: "subs",       label: "Sub-Accounts",     Icon: Users },
  { id: "documents",  label: "Documents",        Icon: FileText },
  { id: "doc-center", label: "Doc Center",       Icon: FolderLock },
  { id: "invoices",   label: "AR Invoices",      Icon: DollarSign },
  { id: "checks",     label: "Check Settlement", Icon: CheckCircle2 },
];

export default function ContractDetailPage() {
  const { contract_id } = useParams();
  const [tab, setTab] = useState("overview");
  const [contract, setContract] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await contractsApi.get(contract_id);
      setContract(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  }, [contract_id]);

  useEffect(() => { reload(); }, [reload]);

  const toggleActive = async () => {
    setBusy(true);
    try {
      await contractsApi.toggleActive(contract_id);
      toast.success("State toggled");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy(false);
    }
  };

  if (!contract) {
    return (
      <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-8 text-center text-zinc-500" data-testid="contract-detail-loading">
        Loading contract {contract_id}…
      </div>
    );
  }

  const label = CLASSIFICATION_LABELS[contract.classification] || contract.classification;

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid={`contract-detail-${contract_id}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider" data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4"/>Admin Console
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <Briefcase className="h-6 w-6 text-emerald-300"/>
            <h1 className="text-xl font-extrabold tracking-tight">{contract.business_name}</h1>
            <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/50 text-[10px] font-mono">{contract.id}</Badge>
            <Badge className={`text-[10px] uppercase tracking-widest font-bold ${contract.active ? "bg-emerald-500/15 text-emerald-200 border-emerald-400/40" : "bg-rose-500/15 text-rose-200 border-rose-400/40"}`}>
              {contract.active ? "Active" : "Inactive"}
            </Badge>
            <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700 text-[10px] uppercase">{label}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!contract.active} onCheckedChange={toggleActive} disabled={busy} data-testid="detail-toggle-active"/>
            <span className="text-xs text-zinc-400">{contract.active ? "Revoke" : "Restore"} access</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-zinc-800 overflow-x-auto">
          {TABS.map((t) => {
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`contract-tab-${t.id}`}
                className={`px-4 py-2 text-xs font-extrabold uppercase tracking-widest transition-colors border-b-2 -mb-[2px] whitespace-nowrap flex items-center gap-1.5 ${
                  isActive ? "text-emerald-200 border-b-emerald-400" : "text-zinc-400 hover:text-zinc-200 border-b-transparent"
                }`}
              >
                <t.Icon className="h-3.5 w-3.5"/>{t.label}
              </button>
            );
          })}
        </div>

        {tab === "overview"  && <OverviewTab contract={contract} reload={reload}/>}
        {tab === "subs"      && <SubAccountsTab contract={contract}/>}
        {tab === "documents" && <DocumentsTab contract={contract}/>}
        {tab === "doc-center" && <DocCenterTab contract={contract}/>}
        {tab === "invoices"  && <InvoicesTab contract={contract}/>}
        {tab === "checks"    && <ChecksTab contract={contract} onChange={reload}/>}
      </div>
    </div>
  );
}

// ─────────────────────────── Overview ────────────────────────────
function OverviewTab({ contract, reload }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    business_name: contract.business_name || "",
    business_website: contract.business_website || "",
    business_address: contract.business_address || "",
    contact_first_name: contract.contact_first_name || "",
    contact_last_name: contract.contact_last_name || "",
    primary_office_phone: contract.primary_office_phone || "",
    mobile_phone: contract.mobile_phone || "",
    notes: contract.notes || "",
    rate_multiplier: contract.rate_multiplier ?? 1.0,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    try {
      await contractsApi.patch(contract.id, {
        ...form,
        rate_multiplier: parseFloat(form.rate_multiplier) || 1.0,
      });
      toast.success("Saved");
      setEdit(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="contract-overview-tab">
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-emerald-400/40 flex-1">Account information</div>
          {!edit ? (
            <Button size="sm" variant="ghost" className="text-emerald-300 hover:text-emerald-200" onClick={() => setEdit(true)} data-testid="overview-edit-btn">Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="text-zinc-300" onClick={() => setEdit(false)}>Cancel</Button>
              <Button size="sm" className="bg-emerald-500 text-zinc-900 hover:bg-emerald-400 font-bold" onClick={save} data-testid="overview-save-btn">Save</Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 text-xs">
          <InfoRow label="Business Name" value={form.business_name} edit={edit} onChange={set("business_name")} testid="ov-business-name"/>
          <InfoRow label="Website" value={form.business_website} edit={edit} onChange={set("business_website")} testid="ov-website"/>
          <InfoRow label="Address" value={form.business_address} edit={edit} onChange={set("business_address")} testid="ov-address" full/>
          <InfoRow label="Contact First Name" value={form.contact_first_name} edit={edit} onChange={set("contact_first_name")} testid="ov-contact-first"/>
          <InfoRow label="Contact Last Name" value={form.contact_last_name} edit={edit} onChange={set("contact_last_name")} testid="ov-contact-last"/>
          <InfoRow label="Office Phone" value={form.primary_office_phone} edit={edit} onChange={set("primary_office_phone")} testid="ov-office-phone"/>
          <InfoRow label="Mobile Phone" value={form.mobile_phone} edit={edit} onChange={set("mobile_phone")} testid="ov-mobile-phone"/>
          <InfoRow label="Rate Multiplier" value={String(form.rate_multiplier)} edit={edit} onChange={set("rate_multiplier")} testid="ov-rate" type="number"/>
          <InfoRow label="Notes" value={form.notes} edit={edit} onChange={set("notes")} testid="ov-notes" full multiline/>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3 self-start">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-amber-400/40">Summary</div>
        <SummaryRow label="Sub-accounts" value={contract.summary?.sub_accounts ?? 0} icon={Users}/>
        <SummaryRow label="Documents" value={contract.summary?.documents ?? 0} icon={FileText}/>
        <SummaryRow label="Invoices" value={contract.summary?.invoices ?? 0} icon={DollarSign}/>
        <SummaryRow label="Open AR Balance" value={`$${(contract.summary?.open_invoice_total ?? 0).toFixed(2)}`} icon={DollarSign} highlight/>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold pt-3 border-t border-zinc-800">Audit trail</div>
        <p className="text-[11px] text-zinc-400">Created {contract.created_at?.slice(0, 19).replace("T", " ")} by {contract.created_by || "—"}</p>
        {contract.updated_at && <p className="text-[11px] text-zinc-400">Updated {contract.updated_at?.slice(0, 19).replace("T", " ")} by {contract.updated_by || "—"}</p>}
        {contract.active_changed_at && <p className="text-[11px] text-zinc-400">State changed {contract.active_changed_at?.slice(0, 19).replace("T", " ")} by {contract.active_changed_by || "—"}</p>}
      </Card>
    </div>
  );
}

function InfoRow({ label, value, edit, onChange, testid, full, multiline, type }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{label}</div>
      {edit ? (
        multiline
          ? <Textarea value={value || ""} onChange={onChange} rows={2} className="bg-zinc-950 border-zinc-700 text-xs"/>
          : <Input type={type || "text"} value={value || ""} onChange={onChange} className="bg-zinc-950 border-zinc-700 h-8 text-xs"/>
      ) : (
        <div className="text-zinc-200 whitespace-pre-wrap">{value || <span className="text-zinc-600">—</span>}</div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, icon: Icon, highlight }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-xs text-zinc-300"><Icon className="h-3.5 w-3.5 text-zinc-500"/>{label}</div>
      <div className={`text-sm font-extrabold ${highlight ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

// ─────────────────────── Sub-Accounts tree ───────────────────────
function SubAccountsTab({ contract }) {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", department: "", contact_name: "", contact_phone: "", contact_email: "", rate_multiplier_override: "" });

  const reload = useCallback(async () => {
    try {
      const { data } = await contractsApi.listSubs(contract.id);
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [contract.id]);
  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = { ...form };
      if (form.rate_multiplier_override === "") delete payload.rate_multiplier_override;
      else payload.rate_multiplier_override = parseFloat(form.rate_multiplier_override);
      await contractsApi.createSub(contract.id, payload);
      toast.success("Sub-account created");
      setShowAdd(false);
      setForm({ name: "", department: "", contact_name: "", contact_phone: "", contact_email: "", rate_multiplier_override: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Delete sub-account "${sub.name}"?`)) return;
    try { await contractsApi.deleteSub(sub.id); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="contract-subs-tab">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-sky-400/40 flex-1">
          Sub-Accounts · embedded tree under {contract.business_name}
        </div>
        <Button size="sm" className="bg-sky-500 text-zinc-900 hover:bg-sky-400 font-bold ml-3" onClick={() => setShowAdd(!showAdd)} data-testid="sub-add-toggle">
          <Plus className="h-3.5 w-3.5 mr-1"/>{showAdd ? "Close" : "Add Sub-Account"}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-4 space-y-2.5" data-testid="sub-add-form">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <SubField label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="sub-name"/>
            <SubField label="Department / Office" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="sub-dept"/>
            <SubField label="Rate Override (optional)" value={form.rate_multiplier_override} onChange={(v) => setForm({ ...form, rate_multiplier_override: v })} testid="sub-rate" type="number"/>
            <SubField label="Contact Name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="sub-contact"/>
            <SubField label="Contact Phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="sub-phone"/>
            <SubField label="Contact Email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="sub-email"/>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-zinc-300">Cancel</Button>
            <Button size="sm" onClick={create} className="bg-emerald-500 text-zinc-900 hover:bg-emerald-400 font-bold" data-testid="sub-create-btn">Create</Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-zinc-500 text-center py-6">No sub-accounts. Parent rate {(contract.rate_multiplier ?? 1).toFixed(2)}× applies to everything.</p>
      ) : (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold pl-3">
            ↳ Parent · {contract.business_name} · {(contract.rate_multiplier ?? 1).toFixed(2)}× rate
          </div>
          {items.map((s) => (
            <div key={s.id} className="ml-4 border-l-2 border-emerald-400/40 pl-3 py-2 bg-zinc-950 rounded-r border-r border-y border-zinc-800 flex items-center justify-between" data-testid={`sub-row-${s.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-zinc-100">{s.name}</span>
                  {s.department && <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700 text-[9px]">{s.department}</Badge>}
                  <Badge className={`text-[9px] font-mono ${s.rate_multiplier_overridden ? "bg-amber-500/15 text-amber-200 border-amber-400/40" : "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>
                    {(s.rate_multiplier ?? 1).toFixed(2)}× {s.rate_multiplier_overridden ? "override" : "inherited"}
                  </Badge>
                </div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  {[s.contact_name, s.contact_phone, s.contact_email].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(s)} className="text-rose-300 hover:text-rose-200" data-testid={`sub-delete-${s.id}`}>
                <Trash2 className="h-3.5 w-3.5"/>
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SubField({ label, value, onChange, testid, type }) {
  return (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{label}</div>
      <Input type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 text-xs"/>
    </div>
  );
}

// ──────────────────────── Documents (200 MB) ─────────────────────
function DocumentsTab({ contract }) {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [labelStr, setLabelStr] = useState("");
  const fileRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await contractsApi.listDocs(contract.id);
      setItems(data.items || []);
      setHistory(data.history || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [contract.id]);
  useEffect(() => { reload(); }, [reload]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("File exceeds 200 MB limit"); return; }
    setUploading(true);
    setProgress(0);
    try {
      await contractsApi.uploadDoc(contract.id, file, labelStr || file.name, (ev) => {
        if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      toast.success(`Uploaded · ${file.name}`);
      setLabelStr("");
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const download = async (doc) => {
    try {
      const { data } = await adminApi.get(`/admin/contracts/documents/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || doc.id;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete document "${doc.label || doc.filename}"? (will be archived for audit, not permanently lost)`)) return;
    try { await contractsApi.deleteDoc(doc.id); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="contract-docs-tab">
      <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-fuchsia-400/40">
          Upload document · up to 200 MB · uncompressed RFP packets supported
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input value={labelStr} onChange={(e) => setLabelStr(e.target.value)} placeholder="Label (optional — defaults to filename)" className="bg-zinc-950 border-zinc-700 text-xs" data-testid="doc-label-input"/>
          <Input ref={fileRef} type="file" onChange={onUpload} disabled={uploading} className="bg-zinc-950 border-zinc-700 text-xs file:text-zinc-300 file:bg-zinc-800 file:border-0 file:px-3 file:py-1.5 file:mr-3 file:rounded md:col-span-2" data-testid="doc-file-input"/>
        </div>
        {uploading && (
          <div className="space-y-1">
            <div className="bg-zinc-800 h-2 rounded overflow-hidden" data-testid="doc-upload-progress">
              <div className="bg-emerald-400 h-full transition-all" style={{ width: `${progress}%` }}/>
            </div>
            <p className="text-[10px] text-zinc-400 font-mono">{progress}% · uploading…</p>
          </div>
        )}
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold px-4 py-2 border-b border-emerald-400/40 bg-zinc-950">Active documents ({items.length})</div>
        {items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-zinc-500 text-center" data-testid="doc-empty">No documents uploaded yet.</p>
        ) : items.map((d) => (
          <div key={d.id} className="px-4 py-2.5 border-b border-zinc-800 last:border-b-0 flex items-center justify-between text-xs" data-testid={`doc-row-${d.id}`}>
            <div>
              <div className="text-zinc-100 font-bold">{d.label || d.filename}</div>
              <div className="text-zinc-500 text-[11px]">
                {d.filename} · {(d.size / (1024 * 1024)).toFixed(2)} MB · v{d.version} · {d.uploaded_at?.slice(0, 19).replace("T", " ")} · {d.uploaded_by}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => download(d)} className="text-emerald-300 hover:text-emerald-200" data-testid={`doc-download-${d.id}`}><Download className="h-3.5 w-3.5 mr-1"/>Download</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(d)} className="text-rose-300 hover:text-rose-200" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5"/></Button>
            </div>
          </div>
        ))}
      </Card>

      {history.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold px-4 py-2 border-b border-zinc-800 bg-zinc-950 flex items-center gap-2">
            <Archive className="h-3 w-3"/>History · audit trail (last {history.length})
          </div>
          {history.map((h) => (
            <div key={h.id} className="px-4 py-1.5 border-b border-zinc-800 last:border-b-0 text-[11px] text-zinc-400 font-mono">
              {h.ts?.slice(0, 19).replace("T", " ")} · <span className="text-amber-300">{h.action}</span> · {h.label || h.doc_id || h.to_doc_id} · by {h.operator}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ────────────────────────── AR Invoices ──────────────────────────
function InvoicesTab({ contract }) {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ open: 0, paid: 0, void: 0 });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ amount: "", description: "", reference: "" });
  const [filter, setFilter] = useState("");

  const reload = useCallback(async () => {
    try {
      const { data } = await contractsApi.listInvoices(contract.id, filter || undefined);
      setItems(data.items || []);
      setTotals(data.totals || { open: 0, paid: 0, void: 0 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [contract.id, filter]);
  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    if (!form.description.trim()) { toast.error("Description required"); return; }
    try {
      await contractsApi.createInvoice(contract.id, { amount: amt, description: form.description.trim(), reference: form.reference.trim() || undefined });
      toast.success("Invoice added");
      setShowAdd(false);
      setForm({ amount: "", description: "", reference: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const markPaid = async (inv) => {
    try { await contractsApi.markInvoicePaid(inv.id); toast.success("Marked paid"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="contract-invoices-tab">
      <div className="grid grid-cols-3 gap-3">
        <TotalCard label="Open" value={`$${totals.open.toFixed(2)}`} accent="amber" testid="invoice-total-open"/>
        <TotalCard label="Paid" value={`$${totals.paid.toFixed(2)}`} accent="emerald" testid="invoice-total-paid"/>
        <TotalCard label="Void" value={`$${totals.void.toFixed(2)}`} accent="zinc" testid="invoice-total-void"/>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-amber-400/40 flex-1">
            Accounts Receivable Ledger
          </div>
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 h-8 text-xs w-32" data-testid="invoice-filter"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-700">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="void">Void</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold" data-testid="invoice-add-toggle">
            <Plus className="h-3.5 w-3.5 mr-1"/>{showAdd ? "Close" : "New Invoice"}
          </Button>
        </div>

        {showAdd && (
          <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end" data-testid="invoice-add-form">
            <SubField label="Amount *" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="invoice-amount" type="number"/>
            <div className="md:col-span-2">
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Description *</div>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-700 h-8 text-xs" data-testid="invoice-description-input"/>
            </div>
            <SubField label="Trip / Waybill ref" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} testid="invoice-ref"/>
            <div className="md:col-span-4 flex justify-end">
              <Button size="sm" onClick={create} className="bg-emerald-500 text-zinc-900 hover:bg-emerald-400 font-bold" data-testid="invoice-create-btn">Create</Button>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-6" data-testid="invoice-empty">No invoices yet.</p>
        ) : (
          <div className="space-y-1.5">
            {items.map((inv) => (
              <div key={inv.id} className={`bg-zinc-950 border-l-4 ${inv.status === "open" ? "border-l-amber-400" : inv.status === "paid" ? "border-l-emerald-400" : "border-l-zinc-600"} px-3 py-2.5 rounded-r border-y border-r border-zinc-800 flex items-center justify-between gap-3`} data-testid={`invoice-row-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-zinc-100">${inv.amount.toFixed(2)}</span>
                    <Badge className={`text-[9px] uppercase font-bold ${inv.status === "open" ? "bg-amber-500/20 text-amber-200 border-amber-400/40" : inv.status === "paid" ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" : "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>{inv.status}</Badge>
                    {inv.reference && <span className="text-[10px] text-zinc-500 font-mono">ref:{inv.reference}</span>}
                  </div>
                  <div className="text-[11px] text-zinc-400 truncate">{inv.description}</div>
                  {inv.paid_via_check_number && (
                    <div className="text-[10px] text-emerald-300">↳ paid via check #{inv.paid_via_check_number} at {inv.paid_at?.slice(0, 19).replace("T", " ")}</div>
                  )}
                </div>
                {inv.status === "open" && (
                  <Button size="sm" variant="ghost" onClick={() => markPaid(inv)} className="text-emerald-300 hover:text-emerald-200" data-testid={`invoice-mark-paid-${inv.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1"/>Mark Paid
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function TotalCard({ label, value, accent, testid }) {
  const accentCls = {
    amber:   "border-amber-400/40 text-amber-200",
    emerald: "border-emerald-400/40 text-emerald-200",
    zinc:    "border-zinc-700 text-zinc-300",
  }[accent] || "border-zinc-700 text-zinc-300";
  return (
    <Card className={`bg-zinc-900 border ${accentCls} p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
      <div className="text-2xl font-extrabold mt-1">{value}</div>
    </Card>
  );
}

// ──────────────────────── Check Settlement ───────────────────────
function ChecksTab({ contract, onChange }) {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ check_number: "", check_amount: "", memo: "" });
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await contractsApi.listChecks({ contract_id: contract.id });
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [contract.id]);
  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    const amt = parseFloat(form.check_amount);
    if (!amt || amt <= 0) { toast.error("Check amount must be > 0"); return; }
    if (!form.check_number.trim()) { toast.error("Check number required"); return; }
    try {
      await contractsApi.createCheck({
        contract_id: contract.id,
        check_number: form.check_number.trim(),
        check_amount: amt,
        memo: form.memo || undefined,
      });
      toast.success("Check logged");
      setShowAdd(false);
      setForm({ check_number: "", check_amount: "", memo: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const transition = async (chk, newStatus) => {
    setBusy(chk.id);
    try {
      const { data } = await contractsApi.patchCheck(chk.id, { status: newStatus });
      if (data.reconciled_count > 0) {
        toast.success(`Check cleared · auto-paid ${data.reconciled_count} invoice${data.reconciled_count === 1 ? "" : "s"}`);
      } else {
        toast.success(`Check → ${newStatus}`);
      }
      reload();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Transition failed"); }
    finally { setBusy(null); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="contract-checks-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-sky-400/40 flex-1">
          Check Settlement Ledger · pending → cleared/bounced (one-way)
        </div>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="bg-sky-500 text-zinc-900 hover:bg-sky-400 font-bold ml-3" data-testid="check-add-toggle">
          <Plus className="h-3.5 w-3.5 mr-1"/>{showAdd ? "Close" : "Log Check"}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end" data-testid="check-add-form">
          <SubField label="Check Number *" value={form.check_number} onChange={(v) => setForm({ ...form, check_number: v })} testid="check-number"/>
          <SubField label="Amount *" value={form.check_amount} onChange={(v) => setForm({ ...form, check_amount: v })} testid="check-amount" type="number"/>
          <div className="md:col-span-2">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Memo / Notes</div>
            <Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} className="bg-zinc-900 border-zinc-700 h-8 text-xs" data-testid="check-memo-input"/>
          </div>
          <div className="md:col-span-4 flex justify-end">
            <Button size="sm" onClick={create} className="bg-emerald-500 text-zinc-900 hover:bg-emerald-400 font-bold" data-testid="check-create-btn">Log Check</Button>
          </div>
        </div>
      )}

      <p className="text-[10px] text-zinc-500 italic">
        Bound to contract <span className="text-emerald-300 font-mono">{contract.id}</span>. When you mark a check as <span className="text-emerald-300">Cleared</span>, any open AR invoices on this contract will be auto-reconciled (oldest-first, FIFO).
      </p>

      {items.length === 0 ? (
        <p className="text-sm text-zinc-500 text-center py-6" data-testid="check-empty">No checks logged yet.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((c) => (
            <div key={c.id} className={`bg-zinc-950 border-l-4 ${c.status === "pending" ? "border-l-amber-400" : c.status === "cleared" ? "border-l-emerald-400" : "border-l-rose-400"} px-3 py-2.5 rounded-r border-y border-r border-zinc-800 flex items-center justify-between gap-3`} data-testid={`check-row-${c.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-zinc-100 font-mono">#{c.check_number}</span>
                  <span className="text-sm text-zinc-200">${c.check_amount.toFixed(2)}</span>
                  <Badge className={`text-[9px] uppercase font-bold ${c.status === "pending" ? "bg-amber-500/20 text-amber-200 border-amber-400/40" : c.status === "cleared" ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" : "bg-rose-500/20 text-rose-200 border-rose-400/40"}`}>
                    {c.status === "pending" ? <Clock className="h-2.5 w-2.5 mr-0.5 inline"/> : c.status === "cleared" ? <CheckCircle2 className="h-2.5 w-2.5 mr-0.5 inline"/> : <XCircle className="h-2.5 w-2.5 mr-0.5 inline"/>}
                    {c.status}
                  </Badge>
                  {c.reconciled_count > 0 && (
                    <Badge className="bg-emerald-900/40 text-emerald-300 border-emerald-500/40 text-[9px]"><RefreshCw className="h-2.5 w-2.5 mr-0.5 inline"/>{c.reconciled_count} invoices auto-paid</Badge>
                  )}
                  {c.unallocated_amount > 0 && (
                    <Badge className="bg-amber-900/40 text-amber-300 border-amber-500/40 text-[9px]"><AlertTriangle className="h-2.5 w-2.5 mr-0.5 inline"/>${c.unallocated_amount.toFixed(2)} unallocated</Badge>
                  )}
                </div>
                {c.memo && <div className="text-[11px] text-zinc-500 mt-0.5">{c.memo}</div>}
              </div>
              {c.status === "pending" && (
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" disabled={busy === c.id} onClick={() => transition(c, "cleared")} className="text-emerald-300 hover:text-emerald-200 disabled:opacity-50" data-testid={`check-clear-${c.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1"/>Cleared
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy === c.id} onClick={() => transition(c, "bounced")} className="text-rose-300 hover:text-rose-200 disabled:opacity-50" data-testid={`check-bounce-${c.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1"/>Bounced
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


// ────────────────────── Doc Center · v2.7.36 ─────────────────────
// Secondary multi-tenant document repository scoped to this contract.
// 100 MB per file, RBAC = dispatch + supervisor + partner + super_user.
// Files isolated server-side under `contracts/{contract_id}/...` path.
function DocCenterTab({ contract }) {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [maxBytes, setMaxBytes] = useState(100 * 1024 * 1024);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [overwriteId, setOverwriteId] = useState(null);
  const [labelStr, setLabelStr] = useState("");
  const [descStr, setDescStr] = useState("");
  const fileRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await contractsApi.listDocCenter(contract.id);
      setItems(data.items || []);
      setHistory(data.history || []);
      if (data.max_bytes) setMaxBytes(data.max_bytes);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [contract.id]);
  useEffect(() => { reload(); }, [reload]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxBytes) { toast.error(`File exceeds ${(maxBytes / (1024 * 1024)) | 0} MB Doc Center limit`); return; }
    setUploading(true);
    setProgress(0);
    try {
      const opts = { label: labelStr || file.name, description: descStr || undefined };
      if (overwriteId) opts.overwriteId = overwriteId;
      const { data } = await contractsApi.uploadDocCenter(contract.id, file, opts, (ev) => {
        if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      toast.success(overwriteId ? `Overwritten · revision ${data.revision}` : `Uploaded · ${file.name}`);
      setLabelStr("");
      setDescStr("");
      setOverwriteId(null);
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const download = async (doc) => {
    try {
      const { data } = await contractsApi.downloadDocCenter(doc.id);
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || doc.id;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  const startOverwrite = (doc) => {
    setOverwriteId(doc.id);
    setLabelStr(doc.label || "");
    setDescStr(doc.description || "");
    toast.info(`Overwrite mode · pick a new file to replace "${doc.label || doc.filename}"`);
    fileRef.current?.click();
  };

  const cancelOverwrite = () => {
    setOverwriteId(null);
    setLabelStr("");
    setDescStr("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.label || doc.filename}" from the Doc Center?`)) return;
    try { await contractsApi.deleteDocCenter(doc.id); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="contract-doc-center-tab">
      <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-sky-400/40 flex-1 flex items-center gap-2">
            <FolderLock className="h-3.5 w-3.5 text-sky-300"/>
            Doc Center · isolated repository · 100 MB / file · dispatch + supervisor + partner + super_user
          </div>
        </div>
        <p className="text-[11px] text-zinc-400">
          Files stored under <code className="text-sky-300">contracts/{contract.id}/...</code> · Doc Center supports per-file in-place <b>overwrite</b> (versioned audit) and is sandboxed away from the primary 200 MB document store.
        </p>

        {overwriteId && (
          <div className="bg-amber-500/10 border border-amber-400/40 rounded-lg px-3 py-2 text-xs text-amber-200 flex items-center justify-between" data-testid="doc-center-overwrite-banner">
            <span><Replace className="h-3.5 w-3.5 inline mr-1"/>Overwrite mode — next upload will replace the selected document in-place.</span>
            <Button size="sm" variant="ghost" className="text-amber-200 hover:text-amber-100" onClick={cancelOverwrite}>Cancel overwrite</Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input value={labelStr} onChange={(e) => setLabelStr(e.target.value)} placeholder="Label (optional)" className="bg-zinc-950 border-zinc-700 text-xs" data-testid="dc-label-input"/>
          <Input value={descStr} onChange={(e) => setDescStr(e.target.value)} placeholder="Description (optional)" className="bg-zinc-950 border-zinc-700 text-xs" data-testid="dc-desc-input"/>
          <Input ref={fileRef} type="file" onChange={onUpload} disabled={uploading} className="bg-zinc-950 border-zinc-700 text-xs file:text-zinc-300 file:bg-zinc-800 file:border-0 file:px-3 file:py-1.5 file:mr-3 file:rounded" data-testid="dc-file-input"/>
        </div>
        {uploading && (
          <div className="space-y-1">
            <div className="bg-zinc-800 h-2 rounded overflow-hidden" data-testid="dc-upload-progress">
              <div className="bg-sky-400 h-full transition-all" style={{ width: `${progress}%` }}/>
            </div>
            <p className="text-[10px] text-zinc-400 font-mono">{progress}% · {overwriteId ? "overwriting" : "uploading"}…</p>
          </div>
        )}
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold px-4 py-2 border-b border-sky-400/40 bg-zinc-950">
          Repository ({items.length})
        </div>
        {items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-zinc-500 text-center" data-testid="dc-empty">
            Doc Center is empty. Drop a file above to begin — Doc Center files are separate from the primary 200 MB document store.
          </p>
        ) : items.map((d) => (
          <div key={d.id} className="px-4 py-2.5 border-b border-zinc-800 last:border-b-0 flex items-center justify-between text-xs hover:bg-zinc-900/60" data-testid={`dc-row-${d.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-zinc-100 font-bold flex items-center gap-2 flex-wrap">
                {d.label || d.filename}
                <Badge className="bg-sky-500/15 text-sky-200 border-sky-400/40 text-[9px] font-mono">rev {d.revision || 1}</Badge>
              </div>
              <div className="text-zinc-500 text-[11px] truncate">
                {d.filename} · {(d.size / (1024 * 1024)).toFixed(2)} MB · {d.uploaded_at?.slice(0, 19).replace("T", " ")} · {d.uploaded_by}
              </div>
              {d.description && <div className="text-zinc-400 text-[11px] italic mt-0.5">{d.description}</div>}
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="ghost" onClick={() => download(d)} className="text-sky-300 hover:text-sky-200" data-testid={`dc-download-${d.id}`}>
                <Download className="h-3.5 w-3.5 mr-1"/>Download
              </Button>
              <Button size="sm" variant="ghost" onClick={() => startOverwrite(d)} className="text-amber-300 hover:text-amber-200" data-testid={`dc-overwrite-${d.id}`}>
                <Replace className="h-3.5 w-3.5 mr-1"/>Overwrite
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(d)} className="text-rose-300 hover:text-rose-200" data-testid={`dc-delete-${d.id}`}>
                <Trash2 className="h-3.5 w-3.5"/>
              </Button>
            </div>
          </div>
        ))}
      </Card>

      {history.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold px-4 py-2 border-b border-zinc-800 bg-zinc-950 flex items-center gap-2">
            <Archive className="h-3 w-3"/>Doc Center history · audit trail (last {history.length})
          </div>
          {history.map((h) => (
            <div key={h.id} className="px-4 py-1.5 border-b border-zinc-800 last:border-b-0 text-[11px] text-zinc-400 font-mono" data-testid={`dc-history-${h.id}`}>
              {h.ts?.slice(0, 19).replace("T", " ")} · <span className={`font-bold ${h.action === "upload" ? "text-emerald-300" : h.action === "overwrite" ? "text-amber-300" : "text-rose-300"}`}>{h.action}</span> · {h.filename || h.to_filename || h.doc_id} · by {h.operator}
              {h.from_revision && h.to_revision && <span className="text-zinc-500"> · rev {h.from_revision} → {h.to_revision}</span>}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
